// ─── LearnerDashboard.jsx ────────────────────────────────────────
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import apiService from '../services/apiService';
import envConfig from '../config/envConfig';
import { ExpertCard, SectionHeader, BookingBadge, EmptyState, Avatar, Modal } from '../components/UI';
import { Search, Calendar, Clock, Video, LogOut, CreditCard, X } from 'lucide-react';
import LearnerProfile from './LearnerProfile';
import {
  LiveKitRoom, GridLayout, ParticipantTile, useTracks,
  RoomAudioRenderer, TrackToggle, DisconnectButton,
  useChat, useLocalParticipant,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';

export function LearnerDashboard() {
  const { user, bookings, categories, experts, toast, logout, refreshBookings } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('learner_tab') || 'discover');
  const [search, setSearch] = useState('');
  const [selCat, setSelCat] = useState('');
  const [selSubcat, setSelSubcat] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [myPacks, setMyPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packsLoaded, setPacksLoaded] = useState(false);
  const [reviewModal, setReviewModal] = useState(null); // { bookingId, expertName }
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [submittedReviews, setSubmittedReviews] = useState({}); // bookingId -> review obj

  // Requirements
  const [requirements, setRequirements] = useState([]);
  const [showReqModal, setShowReqModal] = useState(false);
  const [reqForm, setReqForm] = useState({ title: '', description: '', email: '', phone: '' });
  const [reqSubmitting, setReqSubmitting] = useState(false);

  // Refresh bookings from server when dashboard mounts so learner sees confirmed sessions
  useEffect(() => { refreshBookings(); }, []);

  // Auto-open requirement modal if navigated here with openReqModal state
  useEffect(() => {
    if (location.state?.openReqModal) {
      setShowReqModal(true);
      window.history.replaceState({}, '');
    }
  }, []);

  const myBookings = bookings.filter(b => String(b.receiverId) === String(user?.id));
  const upcoming = myBookings.filter(b => ['pending','confirmed'].includes(b.status));
  const history  = myBookings.filter(b => b.status === 'completed');

  const subcats = useMemo(() => {
    if (!selCat) return [];
    return (categories.find(c => c.id === selCat)?.subs || []);
  }, [selCat, categories]);

  const filtered = useMemo(() => {
    return experts.filter(e => {
      const catMatch = !selCat || String(e.catId) === String(selCat);
      const subcatMatch = !selSubcat || (e.subcategories || []).includes(selSubcat);
      const searchMatch = !search
        || e.name.toLowerCase().includes(search.toLowerCase())
        || (e.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
      return catMatch && subcatMatch && searchMatch;
    });
  }, [search, selCat, selSubcat, experts, subcats]);

  const handleExpertClick = (expert) => {
    // Navigate to expert profile - matching Landing page flow
    navigate(`/expert/${expert.id}`);
  };


  const handleTabClick = (tabId) => {
    if (tabId === 'logout') {
      localStorage.removeItem('learner_tab');
      logout();
      navigate('/');
      toast('Logged out successfully', 'success');
    } else {
      localStorage.setItem('learner_tab', tabId);
      setActiveTab(tabId);
    }
  };

  // Load payment history when payments tab is first visited
  useEffect(() => {
    if (activeTab === 'payments' && !paymentsLoaded) {
      setPaymentsLoading(true);
      apiService.getPaymentHistory()
        .then(res => {
          if (res.success) setPaymentHistory(res.data);
          setPaymentsLoaded(true);
        })
        .catch(err => { console.error('Failed to load payment history:', err); setPaymentsLoaded(true); })
        .finally(() => setPaymentsLoading(false));
    }
  }, [activeTab, paymentsLoaded]);

  // Load existing reviews for history tab
  useEffect(() => {
    if (activeTab === 'history' && history.length > 0) {
      const unreviewed = history.filter(b => submittedReviews[b.id] === undefined);
      unreviewed.forEach(b => {
        apiService.getBookingReview(b.id)
          .then(res => {
            if (res.data) setSubmittedReviews(prev => ({ ...prev, [b.id]: res.data }));
            else setSubmittedReviews(prev => ({ ...prev, [b.id]: null }));
          })
          .catch(() => {});
      });
    }
  }, [activeTab, history.length]);

  // Load my packs when packs tab is first visited
  useEffect(() => {
    if (activeTab === 'packs' && !packsLoaded) {
      setPacksLoading(true);
      apiService.getMyPacks()
        .then(res => { if (res.success) setMyPacks(res.data); setPacksLoaded(true); })
        .catch(() => setPacksLoaded(true))
        .finally(() => setPacksLoading(false));
    }
  }, [activeTab, packsLoaded]);

  const handlePostRequirement = async () => {
    if (!reqForm.title.trim()) { toast('Enter a title for your requirement', 'error'); return; }
    setReqSubmitting(true);
    try {
      const res = await apiService.postRequirement(reqForm);
      if (res.success) {
        setRequirements(prev => [res.data, ...prev]);
        setReqForm({ title: '', description: '', email: '', phone: '' });
        setShowReqModal(false);
        toast('Requirement posted! Experts will reach out to you ✓', 'success');
      }
    } catch (err) {
      toast(err.message || 'Failed to post requirement', 'error');
    } finally {
      setReqSubmitting(false);
    }
  };

  const SIDEBAR = [
    { id: 'discover',  label: 'Discover',    icon: '🔍' },
    { id: 'sessions',  label: 'My Sessions', icon: '📅' },
    { id: 'packs',     label: 'My Packs',    icon: '📦' },
    { id: 'history',   label: 'History',     icon: '⏱' },
    { id: 'payments',  label: 'Payments',    icon: <CreditCard size={16} /> },
    { id: 'profile',   label: 'My Profile',  icon: '👤' },
    { id: 'logout',    label: 'Logout',      icon: <LogOut size={16} /> },
  ];

  return (
    <div className="dashboard-layout" style={{ display: 'flex', minHeight: 'calc(100vh - 60px)' }}>
      <div className="sidebar">
        {SIDEBAR.map(({ id, label, icon }) => (
          <div
            key={id}
            className={`sidebar-item ${activeTab === id ? 'active' : ''} ${id === 'logout' ? 'logout-item' : ''}`}
            onClick={() => handleTabClick(id)}
            style={id === 'logout' ? { color: '#C13030' } : {}}
          >
            <span>{icon}</span>
            <span className="sidebar-label">{label}</span>
          </div>
        ))}
      </div>
      <div className="dashboard-content" style={{ flex: 1, padding: 28, overflow: 'auto' }}>
        {activeTab === 'discover' && (
          <div className="animate-fadeUp">
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, marginBottom:20 }}>
              <div>
                <h2 style={{ fontWeight:700, fontSize:22, marginBottom:4 }}>Welcome back, {user?.name?.split(' ')[0]} 👋</h2>
                <p style={{ fontSize:13, color:'var(--ink3)', margin:0 }}>Find your next session with an expert.</p>
              </div>
              <button onClick={() => setShowReqModal(true)} className="btn btn-secondary btn-sm" style={{ flexShrink:0, whiteSpace:'nowrap', marginTop:4 }}>
                📋 Post Requirement
              </button>
            </div>

            {/* Multi-filter search bar (matching Landing page) */}
            <div style={{ maxWidth: 860, margin: '0 0 32px', background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
              {/* Top row: keyword search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px', borderBottom: '1px solid var(--border2)' }}>
                <Search size={16} style={{ color: 'var(--ink4)', flexShrink: 0 }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by skill, expert, keyword…"
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: 'var(--ink)', background: 'transparent' }}
                />
                {(search || selCat || selSubcat) && (
                  <button onClick={() => { setSearch(''); setSelCat(''); setSelSubcat(''); }} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--ink4)', background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 999, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <X size={11} /> Clear all
                  </button>
                )}
              </div>

              {/* Bottom row: category dropdown */}
              <div style={{ display: 'flex', alignItems: 'stretch', padding: '4px 14px 8px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--saffron)', textTransform: 'uppercase', letterSpacing: '0.07em', marginRight: 12, display: 'flex', alignItems: 'center' }}>Category</div>
                <select
                  value={selCat}
                  onChange={e => setSelCat(e.target.value)}
                  style={{ border: 'none', outline: 'none', fontSize: 13, color: selCat ? 'var(--ink)' : 'var(--ink4)', background: 'transparent', cursor: 'pointer', fontWeight: selCat ? 600 : 400, appearance: 'none' }}
                >
                  <option value="">All Categories</option>
                  {categories.filter(c => c.enabled).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
            </div>

            {selCat && subcats.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <button
                  onClick={() => setSelSubcat('')}
                  style={{
                    padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1.5px solid ${!selSubcat ? 'var(--saffron)' : 'var(--border)'}`,
                    background: !selSubcat ? 'var(--saffron-lt)' : 'var(--surface)',
                    color: !selSubcat ? 'var(--saffron)' : 'var(--ink3)',
                  }}
                >All</button>
                {subcats.map(s => (
                  <button
                    key={s}
                    onClick={() => setSelSubcat(v => v === s ? '' : s)}
                    style={{
                      padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1.5px solid ${selSubcat === s ? 'var(--teal)' : 'var(--border)'}`,
                      background: selSubcat === s ? 'var(--teal-lt, #e6f7f5)' : 'var(--surface)',
                      color: selSubcat === s ? 'var(--teal)' : 'var(--ink3)',
                    }}
                  >{s}</button>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
              {filtered.map(e => <ExpertCard key={e.id} expert={e} onClick={() => handleExpertClick(e)} />)}
            </div>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>No experts found</div>
                <div style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 24 }}>
                  {experts.length === 0 ? 'No experts available yet.' : "Can't find the right match? Post your requirement and let experts come to you."}
                </div>
                {experts.length > 0 && (
                  <button onClick={() => setShowReqModal(true)} className="btn btn-primary">
                    📋 Post Your Requirement →
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="animate-fadeUp">
            <SectionHeader title="My Upcoming Sessions" />
            {upcoming.length === 0
              ? <EmptyState icon="📅" title="No upcoming sessions" description="Book a session with an expert to get started." action={<button onClick={() => setActiveTab('discover')} className="btn btn-primary btn-sm">Browse Experts →</button>} />
              : upcoming.map(b => (
                  <div key={b.id} style={{ display:'flex',flexDirection:'column',gap:12,padding:'16px',background:'var(--surface)',border:'1px solid var(--border2)',borderRadius:10,marginBottom:12 }}>
                    {/* Trainer Info Section */}
                    <div style={{ display:'flex',alignItems:'center',gap:12,paddingBottom:12,borderBottom:'1px solid var(--border2)' }}>
                      <Avatar src={b.expert_photo} name={b.expert_name} size="sm" />
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:13,color:'var(--ink2)' }}>Trainer</div>
                        <div style={{ fontSize:14,fontWeight:600,marginTop:2 }}>{b.expert_name || 'Expert'}</div>
                      </div>
                      <button onClick={() => navigate(`/expert/${b.expert_id}`)} className="btn btn-sm" style={{ background:'var(--teal)',color:'white',border:'none',padding:'6px 14px',fontSize:12,fontWeight:600 }}>
                        View Profile →
                      </button>
                    </div>

                    {/* Booking Details Section */}
                    <div>
                      <div style={{ fontWeight:600,fontSize:14 }}>{b.service}</div>
                      <div style={{ fontSize:12,color:'var(--ink4)',marginTop:4,display:'flex',gap:16 }}>
                        <span>📅 {b.slot?.date}</span>
                        <span>🕐 {b.slot?.start}–{b.slot?.end}</span>
                        <span style={{ fontWeight:700,color:'var(--saffron)' }}>₹{b.price?.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display:'flex',alignItems:'center',gap:12,justifyContent:'space-between',paddingTop:8 }}>
                      <BookingBadge status={b.status} />
                      {b.status === 'confirmed' && b.videoRoom
                        ? <button onClick={() => navigate(`/session/${b.id}`)} className="btn btn-primary btn-sm" style={{gap:6}}><Video size={13}/> Join Session</button>
                        : b.status === 'pending' && <span style={{fontSize:12,color:'var(--ink4)',fontStyle:'italic'}}>⏳ Awaiting trainer confirmation</span>
                      }
                    </div>
                  </div>
                ))
            }
          </div>
        )}

        {activeTab === 'packs' && (
          <div className="animate-fadeUp">
            <SectionHeader title="My Session Packs" />
            {packsLoading ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--ink3)' }}>Loading packs…</div>
            ) : myPacks.length === 0 ? (
              <EmptyState icon="📦" title="No packs yet" description="Buy a session pack from an expert's profile to get bulk sessions at a discounted rate." />
            ) : myPacks.map(p => {
              const remaining = p.sessions_total - p.sessions_used;
              const pct = Math.round((p.sessions_used / p.sessions_total) * 100);
              const isExpired = p.status === 'expired' || p.status === 'exhausted' || new Date(p.expiry_date) < new Date();
              const expiryFmt = new Date(p.expiry_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
              return (
                <div key={p.id} style={{ background:'var(--surface)', border:`1.5px solid ${isExpired?'var(--border2)':remaining===0?'var(--border2)':'var(--teal)'}`, borderRadius:12, padding:'16px 20px', marginBottom:12, opacity: isExpired?0.65:1 }}>
                  <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
                    {p.expert_image
                      ? <img src={p.expert_image} alt="" style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
                      : <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--teal)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700, fontSize:16, flexShrink:0 }}>{(p.expert_name||'E')[0]}</div>
                    }
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{p.title}</div>
                      <div style={{ fontSize:12, color:'var(--ink3)', marginBottom:10 }}>with {p.expert_name}</div>
                      <div style={{ background:'var(--bg2)', borderRadius:6, height:8, marginBottom:6, overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`, height:'100%', background: isExpired?'var(--border2)':'var(--teal)', borderRadius:6, transition:'width 0.4s' }} />
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--ink3)', marginBottom:8 }}>
                        <span>{p.sessions_used} used</span>
                        <span>{remaining} remaining of {p.sessions_total}</span>
                      </div>
                      <div style={{ display:'flex', gap:16, fontSize:12, color:'var(--ink3)' }}>
                        <span>💰 Paid ₹{Number(p.amount_paid).toLocaleString()}</span>
                        <span>{isExpired ? '❌ Expired' : `⏳ Expires ${expiryFmt}`}</span>
                      </div>
                    </div>
                    <div style={{ flexShrink:0, textAlign:'right' }}>
                      {!isExpired && remaining > 0 && (
                        <button onClick={() => navigate(`/expert/${p.expert_id}`)} className="btn btn-primary btn-sm">
                          Book Session →
                        </button>
                      )}
                      {(isExpired || remaining === 0) && (
                        <span style={{ fontSize:11, padding:'3px 10px', borderRadius:10, background:'var(--bg2)', color:'var(--ink4)', fontWeight:600 }}>
                          {remaining === 0 ? 'Exhausted' : 'Expired'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Session History" />
            {history.length === 0
              ? <EmptyState icon="📋" title="No completed sessions yet." />
              : history.map(b => {
                const existingReview = submittedReviews[b.id];
                return (
                  <div key={b.id} style={{ display:'flex',alignItems:'center',gap:14,padding:'14px 18px',background:'var(--surface)',border:'1px solid var(--border2)',borderRadius:10,marginBottom:10 }}>
                    <Avatar src={b.expert_photo} name={b.expert_name} size="sm" />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600,fontSize:14 }}>{b.service} <span style={{ color:'var(--ink3)',fontWeight:400 }}>with {b.expert_name || 'Expert'}</span></div>
                      <div style={{ fontSize:12,color:'var(--ink4)',marginTop:2 }}>{b.slot?.date} · {b.slot?.start}–{b.slot?.end}</div>
                      {existingReview && (
                        <div style={{ marginTop:4, fontSize:12, color:'var(--ink3)' }}>
                          {'★'.repeat(existingReview.rating)}{'☆'.repeat(5-existingReview.rating)}
                          {existingReview.comment && <span style={{ marginLeft:6, fontStyle:'italic' }}>"{existingReview.comment}"</span>}
                        </div>
                      )}
                    </div>
                    <BookingBadge status="completed" />
                    <div style={{ fontWeight:700 }}>₹{b.price?.toLocaleString()}</div>
                    {existingReview ? (
                      <span style={{ fontSize:12,color:'var(--teal)',fontWeight:600 }}>✓ Reviewed</span>
                    ) : (
                      <button
                        className="btn btn-ghost-saffron btn-sm"
                        onClick={() => {
                          setReviewModal({ bookingId: b.id, expertName: b.expert_name });
                          setReviewRating(0);
                          setReviewComment('');
                          setReviewHover(0);
                        }}
                      >★ Review</button>
                    )}
                  </div>
                );
              })
            }
          </div>
        )}

        {/* Review Modal */}
        {reviewModal && (
          <Modal
            open={!!reviewModal}
            onClose={() => setReviewModal(null)}
            title={`Review session with ${reviewModal.expertName || 'Expert'}`}
          >
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--ink2)' }}>Rating</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      onMouseEnter={() => setReviewHover(star)}
                      onMouseLeave={() => setReviewHover(0)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, color: star <= (reviewHover || reviewRating) ? 'var(--saffron)' : 'var(--border)', padding: 0, lineHeight:1 }}
                    >★</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--ink2)' }}>Comment <span style={{ fontWeight:400,color:'var(--ink4)' }}>(optional)</span></div>
                <textarea
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  placeholder="Share your experience with this expert…"
                  rows={3}
                  style={{ width: '100%', border: '1px solid var(--border2)', borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setReviewModal(null)}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!reviewRating || reviewSubmitting}
                  onClick={async () => {
                    if (!reviewRating) { toast('Please select a rating', 'error'); return; }
                    setReviewSubmitting(true);
                    try {
                      const res = await apiService.submitReview(reviewModal.bookingId, reviewRating, reviewComment);
                      setSubmittedReviews(prev => ({ ...prev, [reviewModal.bookingId]: res.data }));
                      toast('Review submitted!', 'success');
                      setReviewModal(null);
                    } catch (err) {
                      toast(err.message || 'Failed to submit review', 'error');
                    } finally {
                      setReviewSubmitting(false);
                    }
                  }}
                >{reviewSubmitting ? 'Submitting…' : 'Submit Review'}</button>
              </div>
            </div>
          </Modal>
        )}

        {activeTab === 'payments' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Payment History" />
            {paymentsLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)' }}>Loading payments…</div>
            ) : paymentHistory.length === 0 ? (
              <EmptyState icon="💳" title="No payments yet" description="Your payment history will appear here once you book a session." />
            ) : (
              <>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 4 }}>Total Spent</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--saffron)' }}>
                      ₹{paymentHistory.reduce((sum, p) => sum + Number(p.amount || 0), 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 4 }}>{paymentHistory.length} sessions</div>
                  </div>
                </div>

                {/* Payments Table */}
                <div className="card" style={{ overflow: 'hidden' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Expert</th>
                        <th>Service</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((p, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: 12 }}>
                            {new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td style={{ fontWeight: 600 }}>{p.expert_name || '—'}</td>
                          <td style={{ fontSize: 13, color: 'var(--ink3)' }}>{p.service_title || '—'}</td>
                          <td style={{ fontWeight: 700, color: 'var(--saffron)' }}>₹{Number(p.amount).toLocaleString()}</td>
                          <td>
                            <BookingBadge status={p.booking_status === 'completed' ? 'completed' : p.status === 'paid' ? 'confirmed' : 'pending'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <LearnerProfile />
        )}
      </div>

      {/* Post Requirement Modal */}
      <Modal
        open={showReqModal}
        onClose={() => { setShowReqModal(false); setReqForm({ title: '', description: '', email: '', phone: '' }); }}
        title="📋 Post Your Requirement"
        subtitle="Describe what you're looking for — matching experts will reach out to you."
      >
        <div style={{ padding: '4px 0' }}>
          <div className="form-group">
            <label className="form-label">What do you want to learn? *</label>
            <input className="form-input" placeholder="e.g. IELTS preparation, Python for beginners…" value={reqForm.title} onChange={e => setReqForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">More details <span style={{ fontWeight:400, color:'var(--ink4)' }}>(optional)</span></label>
            <textarea className="form-textarea" placeholder="Your current level, specific goals, preferred teaching style…" value={reqForm.description} onChange={e => setReqForm(f => ({ ...f, description: e.target.value }))} rows={3} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" placeholder="your@email.com" value={reqForm.email} onChange={e => setReqForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone <span style={{ fontWeight:400, color:'var(--ink4)' }}>(optional)</span></label>
              <input className="form-input" type="tel" placeholder="+91 98765 43210" value={reqForm.phone} onChange={e => setReqForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={handlePostRequirement} disabled={reqSubmitting} className="btn btn-primary" style={{ flex: 1 }}>
              {reqSubmitting ? 'Posting…' : '📤 Post Requirement'}
            </button>
            <button onClick={() => { setShowReqModal(false); setReqForm({ title: '', description: '', email: '', phone: '' }); }} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Patches getDisplayMedia so the current browser tab is excluded from the
// screen picker — prevents the infinite mirror loop when screen sharing.
function useExcludeCurrentTabFromScreenShare() {
  useEffect(() => {
    if (!navigator.mediaDevices?.getDisplayMedia) return;
    const original = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
    navigator.mediaDevices.getDisplayMedia = (constraints = {}) =>
      original({ ...constraints, selfBrowserSurface: 'exclude' });
    return () => {
      navigator.mediaDevices.getDisplayMedia = original;
    };
  }, []);
}

// Draggable chat popup — must be inside <LiveKitRoom>
function ChatPopup({ open, onClose, onNewMessage }) {
  const { chatMessages, send } = useChat();
  const { localParticipant } = useLocalParticipant();
  const [input, setInput] = useState('');
  const [pos, setPos] = useState(() => ({
    x: Math.max(0, (window.innerWidth / 2) - 160),
    y: Math.max(0, window.innerHeight - 500),
  }));
  const dragging = useRef(false);
  const dragOff = useRef({ x: 0, y: 0 });
  const endRef = useRef(null);
  const prevLen = useRef(0);

  useEffect(() => {
    if (chatMessages.length > prevLen.current && !open) onNewMessage?.();
    prevLen.current = chatMessages.length;
  }, [chatMessages.length, open]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, open]);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOff.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 440, e.clientY - dragOff.current.y)),
      });
    };
    const up = () => { dragging.current = false; };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, []);

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    await send(text);
  };

  if (!open) return null;

  return (
    <div style={{
      position:'fixed', left:pos.x, top:pos.y, zIndex:200,
      width:320, height:430,
      background:'#1A1208', border:'1px solid #3C3018',
      borderRadius:14, display:'flex', flexDirection:'column',
      boxShadow:'0 8px 40px rgba(0,0,0,0.7)', overflow:'hidden',
    }}>
      {/* Drag handle / header */}
      <div
        onMouseDown={e => { dragging.current = true; dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; }}
        style={{
          padding:'10px 14px', borderBottom:'1px solid #2C2218',
          background:'#120E0A', display:'flex', alignItems:'center',
          justifyContent:'space-between', cursor:'grab', userSelect:'none',
        }}
      >
        <span style={{ fontSize:13, fontWeight:700, color:'#F7F4EF', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:16, opacity:0.5 }}>⠿</span> Session Chat
        </span>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'#6B5B4E', cursor:'pointer', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
      </div>

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:'10px', display:'flex', flexDirection:'column', gap:8 }}>
        {chatMessages.length === 0 ? (
          <div style={{ color:'#4B3B2E', fontSize:12, textAlign:'center', marginTop:60 }}>No messages yet. Say hi! 👋</div>
        ) : chatMessages.map((m, i) => {
          const isMine = m.from?.identity === localParticipant?.identity;
          return (
            <div key={m.id || i} style={{ display:'flex', flexDirection:'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
              {!isMine && m.from?.name && (
                <span style={{ fontSize:10, color:'#9B9088', marginBottom:2, marginLeft:6 }}>{m.from.name}</span>
              )}
              <div style={{
                maxWidth:'82%', wordBreak:'break-word',
                background: isMine ? '#0B6E65' : '#2C2218',
                color:'#F7F4EF', fontSize:13, lineHeight:1.5,
                padding:'7px 11px',
                borderRadius: isMine ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
              }}>
                {m.message}
              </div>
              <span style={{ fontSize:10, color:'#4B3B2E', marginTop:2, marginLeft:6, marginRight:6 }}>
                {new Date(m.timestamp).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })}
              </span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding:'8px 10px', borderTop:'1px solid #2C2218', display:'flex', gap:6 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Type a message…"
          style={{
            flex:1, background:'rgba(255,255,255,0.06)', border:'1px solid #3C3018',
            borderRadius:8, padding:'7px 10px', color:'#F7F4EF',
            fontSize:13, outline:'none', fontFamily:'var(--font-body)',
          }}
        />
        <button onClick={handleSend} disabled={!input.trim()} style={{
          background: input.trim() ? '#0B6E65' : 'rgba(255,255,255,0.06)',
          border:'none', borderRadius:8, padding:'7px 12px',
          color: input.trim() ? '#fff' : '#4B3B2E',
          fontSize:15, cursor: input.trim() ? 'pointer' : 'default', transition:'all 0.15s',
        }}>↑</button>
      </div>
    </div>
  );
}

// Custom room layout — inside <LiveKitRoom>, gives full control over control bar order
function SessionRoom({ onDisconnect }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  const toggleChat = () => {
    setChatOpen(o => !o);
    setUnread(0);
  };

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <GridLayout tracks={tracks} style={{ flex:1, minHeight:0 }}>
        <ParticipantTile />
      </GridLayout>

      {/* Custom control bar — Mic | Camera | Chat | Screen Share | Leave */}
      <div className="lk-control-bar">
        <TrackToggle source={Track.Source.Microphone}>Microphone</TrackToggle>
        <TrackToggle source={Track.Source.Camera}>Camera</TrackToggle>

        <button
          onClick={toggleChat}
          className="lk-button"
          style={chatOpen ? { background:'rgba(11,110,101,0.28)', borderColor:'#0B6E65', color:'#5DD4CB' } : {}}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Chat
          {unread > 0 && !chatOpen && (
            <span style={{
              background:'#C13030', color:'#fff', fontSize:10, fontWeight:700,
              borderRadius:'50%', width:16, height:16, marginLeft:2,
              display:'inline-flex', alignItems:'center', justifyContent:'center',
            }}>{unread > 9 ? '9+' : unread}</span>
          )}
        </button>

        <TrackToggle source={Track.Source.ScreenShare}>Share Screen</TrackToggle>
        <DisconnectButton onClick={onDisconnect}>Leave</DisconnectButton>
      </div>

      <ChatPopup
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onNewMessage={() => setUnread(u => u + 1)}
      />
      <RoomAudioRenderer />
    </div>
  );
}

// ─── VideoSession.jsx ────────────────────────────────────────────
export function VideoSession() {
  const { bookings, user, refreshBookings } = useApp();
  const navigate = useNavigate();
  const { id: bookingId } = useParams();
  const [notes, setNotes] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lkToken, setLkToken] = useState(null);
  const [lkWsUrl, setLkWsUrl] = useState(null);
  const [lkError, setLkError] = useState(null);
  const [lkLoading, setLkLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isTooEarly, setIsTooEarly] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  useExcludeCurrentTabFromScreenShare();

  const booking = bookings.find(b => String(b.id) === String(bookingId));

  const otherPerson = booking
    ? (user?.role === 'expert' ? (booking.learner_name || 'Learner') : (booking.expert_name || 'Expert'))
    : '';

  useEffect(() => {
    if (!booking?.videoRoom || !booking?.slot) return;

    // Parse slot start/end into full Date objects (local timezone)
    const parseDateTime = (dateStr, timeStr) => {
      try {
        // Ensure time has seconds
        const t = timeStr.includes(':') ? `${timeStr}:00` : `${timeStr}:00`;
        return new Date(`${dateStr}T${t}`);
      } catch (e) { return null; }
    };

    const startDt = parseDateTime(booking.slot.date, booking.slot.start);
    const endDt = parseDateTime(booking.slot.date, booking.slot.end);
    const now = new Date();

    const fetchToken = () => {
      setLkLoading(true);
      setLkError(null);
      apiService.getLiveKitToken(booking.videoRoom)
        .then(res => {
          if (res.success) {
            setLkToken(res.data.token);
            setLkWsUrl(res.data.wsUrl);
          } else {
            setLkError(res.message || 'Failed to get session token');
          }
        })
        .catch(err => setLkError(err.message || 'Failed to connect'))
        .finally(() => setLkLoading(false));
    };

    if (endDt && now > endDt) {
      // Meeting finished
      setIsExpired(true);
      setIsTooEarly(false);
      setTimeLeft(null);
      return;
    }

    if (startDt && now < startDt) {
      // Too early — show countdown until start
      setIsTooEarly(true);
      setIsExpired(false);
      setTimeLeft(startDt - now);

      const iv = setInterval(() => {
        const now2 = new Date();
        const diff = startDt - now2;
        if (diff <= 0) {
          clearInterval(iv);
          setIsTooEarly(false);
          setTimeLeft(null);
          fetchToken();
        } else {
          setTimeLeft(diff);
        }
      }, 1000);

      return () => clearInterval(iv);
    }

    // Within meeting window — fetch token
    setIsTooEarly(false);
    setIsExpired(false);
    fetchToken();
  }, [booking?.videoRoom, booking?.slot?.date, booking?.slot?.start, booking?.slot?.end]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshBookings();
    setRefreshing(false);
  };

  if (!booking?.videoRoom) {
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'calc(100vh - 60px)',flexDirection:'column',gap:16 }}>
        <div style={{ fontSize:36 }}>🔒</div>
        <div style={{ fontSize:18,fontWeight:700 }}>Video room not available</div>
        <div style={{ fontSize:13,color:'var(--ink3)',textAlign:'center',maxWidth:320 }}>
          {booking ? 'The expert has not confirmed this session yet. Refresh to check for updates.' : 'Session not found.'}
        </div>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={handleRefresh} disabled={refreshing} className="btn btn-primary">
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">← Go back</button>
        </div>
      </div>
    );
  }

  if (lkLoading) {
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'calc(100vh - 60px)',flexDirection:'column',gap:12 }}>
        <div style={{ width:36,height:36,border:'3px solid var(--sage)',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite' }} />
        <div style={{ fontSize:14,color:'var(--ink3)' }}>Connecting to session…</div>
      </div>
    );
  }

  if (isTooEarly && timeLeft != null) {
    const fmt = (ms) => {
      const s = Math.max(0, Math.floor(ms/1000));
      const hh = Math.floor(s/3600);
      const mm = Math.floor((s%3600)/60);
      const ss = s%60;
      return `${hh>0?hh+':':''}${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
    };
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'calc(100vh - 60px)',flexDirection:'column',gap:12 }}>
        <div style={{ fontSize:36 }}>⏳</div>
        <div style={{ fontSize:18,fontWeight:700 }}>Meeting starts soon</div>
        <div style={{ fontSize:14,color:'var(--ink3)' }}>You can join when the timer reaches zero.</div>
        <div style={{ marginTop:12, fontSize:22, fontWeight:700 }}>{fmt(timeLeft)}</div>
        <div style={{ marginTop:16 }}>
          <button onClick={() => window.location.reload()} className="btn btn-secondary">Refresh</button>
          <button onClick={() => navigate(-1)} className="btn btn-secondary" style={{ marginLeft:8 }}>← Back</button>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'calc(100vh - 60px)',flexDirection:'column',gap:16 }}>
        <div style={{ fontSize:36 }}>⛔</div>
        <div style={{ fontSize:18,fontWeight:700 }}>Meeting link expired</div>
        <div style={{ fontSize:13,color:'var(--ink3)',textAlign:'center',maxWidth:320 }}>This session has already ended. If you believe this is a mistake, please contact support or refresh bookings.</div>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={handleRefresh} disabled={refreshing} className="btn btn-primary">{refreshing ? 'Refreshing…' : '↻ Refresh'}</button>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">← Go back</button>
        </div>
      </div>
    );
  }

  if (lkError) {
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'calc(100vh - 60px)',flexDirection:'column',gap:16 }}>
        <div style={{ fontSize:36 }}>⚠️</div>
        <div style={{ fontSize:18,fontWeight:700 }}>Could not connect</div>
        <div style={{ fontSize:13,color:'var(--ink3)',textAlign:'center',maxWidth:320 }}>{lkError}</div>
        <button onClick={() => navigate(-1)} className="btn btn-secondary">← Go back</button>
      </div>
    );
  }

  return (
    <div className="video-screen">
      <div className="video-main">
        {lkToken && lkWsUrl ? (
          <LiveKitRoom
            serverUrl={lkWsUrl}
            token={lkToken}
            connect={true}
            video={true}
            audio={true}
            onDisconnected={() => navigate(-1)}
            onMediaDeviceFailure={() => {}}
            style={{ height:'100%', width:'100%', display:'flex', flexDirection:'column' }}
          >
            <SessionRoom onDisconnect={() => navigate(-1)} />
          </LiveKitRoom>
        ) : null}
      </div>
      <div className="video-panel">
        <div>
          <div style={{ fontFamily:'var(--font-display)',fontSize:16,fontWeight:700,color:'white',marginBottom:4 }}>{booking.service}</div>
          <div style={{ fontSize:12,color:'#9B9088' }}>with {otherPerson}</div>
          <div style={{ display:'flex',gap:5,marginTop:8,flexWrap:'wrap' }}>
            <span style={{ background:'rgba(212,114,12,0.25)',color:'#F4A84A',fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:999 }}>{booking.slot.date}</span>
            <span style={{ background:'rgba(11,110,101,0.25)',color:'#5DD4CB',fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:999 }}>{booking.slot.start}–{booking.slot.end}</span>
          </div>
        </div>
        <hr style={{ borderColor:'#2C2218' }} />
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12,fontWeight:700,color:'#F7F4EF',marginBottom:8 }}>📝 Session Notes</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            style={{ width:'100%',background:'rgba(255,255,255,0.06)',border:'1px solid #3C3018',color:'#D8D2C8',fontSize:13,lineHeight:1.6,resize:'none',height:'calc(100vh - 320px)',outline:'none',borderRadius:8,padding:12,fontFamily:'var(--font-body)' }}
            placeholder="Take notes during your session…" />
        </div>
        <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
          <button onClick={() => navigate(-1)} className="btn btn-secondary btn-sm" style={{ background:'#3C3018',color:'#D8D2C8',border:'none',justifyContent:'center' }}>← Back</button>
          <button onClick={() => navigate(-1)} className="btn btn-danger btn-sm" style={{ justifyContent:'center' }}>End Session</button>
        </div>
      </div>
    </div>
  );
}

function EnableToggle({ enabled, onChange }) {
  return (
    <label className="toggle" style={{ cursor:'pointer' }}>
      <input type="checkbox" checked={!!enabled} onChange={e => onChange(e.target.checked)} style={{ display:'none' }} />
      <span className="toggle-slider" />
    </label>
  );
}

// ─── AdminPanel.jsx ──────────────────────────────────────────────
export function AdminPanel() {
  const { categories, toggleCategory, priceTiers, updatePriceTier, toast, logout } = useApp();
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('admin_tab') || 'dashboard');
  const [experts, setExperts] = useState([]);
  const [learners, setLearners] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  // Language manager
  const [languages, setLanguages] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showLangModal, setShowLangModal] = useState(false);
  const [langForm, setLangForm] = useState({ name:'', code:'' });
  const [editingLang, setEditingLang] = useState(null);
  // Category manager
  const [catData, setCatData] = useState([]);
  const [showCatModal, setShowCatModal] = useState(false);
  const [catModalForm, setCatModalForm] = useState({ name:'', icon:'' });
  const [editingCat, setEditingCat] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null);
  // Subcategory manager
  const [showSubModal, setShowSubModal] = useState(false);
  const [subModalCatId, setSubModalCatId] = useState(null);
  const [subForm, setSubForm] = useState({ name:'' });
  const [editingSub, setEditingSub] = useState(null);
  // Tag manager
  const [tagData, setTagData] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagForm, setTagForm] = useState({ name:'' });
  const [editingTag, setEditingTag] = useState(null);
  // Booking filter
  const [adminBookingFilter, setAdminBookingFilter] = useState('all');
  // Earnings
  const [earningsData, setEarningsData] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(false);
  // Dashboard stats
  const [adminStats, setAdminStats] = useState(null);
  // Admin bookings (separate from context — context only loads for expert/learner)
  const [adminBookings, setAdminBookings] = useState([]);
  const [adminBookingsLoading, setAdminBookingsLoading] = useState(false);
  // Admin requirements
  const [adminRequirements, setAdminRequirements] = useState([]);
  const [adminReqLoading, setAdminReqLoading] = useState(false);
  // Commission editing
  const [editingCommission, setEditingCommission] = useState(null); // { expertId, value }
  const [commissionSaving, setCommissionSaving] = useState(false);
  // Catalogue (dummy experts)
  const [catalogueExperts, setCatalogueExperts] = useState([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  // Admin settings
  const [adminPasswordForm, setAdminPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [adminSecurityForm, setAdminSecurityForm] = useState({ question: '', answer: '' });
  const [adminCustomerCareNumbers, setAdminCustomerCareNumbers] = useState([{ country: 'India', phone: '+91-1234567890', whatsapp: '' }]);

  const SIDEBAR = [
    { id:'dashboard',   label:'Dashboard',         icon:'⊞' },
    { id:'languages',   label:'Languages',          icon:'🌐' },
    { id:'categories',  label:'Categories',         icon:'⊡' },
    { id:'pricing',     label:'Price Enumeration',  icon:'₹' },
    { id:'experts',     label:'Experts',            icon:'🎓' },
    { id:'learners',    label:'Learners',           icon:'📚' },
    { id:'tags',        label:'Tags',               icon:'🏷️' },
    { id:'catalogue',    label:'Catalogue',         icon:'◈' },
    { id:'bookings',      label:'All Bookings',   icon:'📋' },
    { id:'requirements',  label:'Requirements',   icon:'📩' },
    { id:'earnings',      label:'Earnings',       icon:'💰' },
    { id:'settings',      label:'Settings',       icon:'⚙️' },
    { id:'logout',        label:'Logout',         icon:<LogOut size={16} /> },
  ];

  React.useEffect(() => {
    if (activeTab === 'experts' || activeTab === 'learners') {
      fetchUsers();
    }
    if (activeTab === 'earnings') {
      setEarningsLoading(true);
      apiService.getAdminExpertEarnings()
        .then(res => { if (res.success) setEarningsData(res.data); })
        .catch(err => toast(err.message || 'Failed to load earnings', 'error'))
        .finally(() => setEarningsLoading(false));
    }
    if (activeTab === 'dashboard') {
      apiService.getAdminStats()
        .then(res => { if (res.success) setAdminStats(res.data); })
        .catch(() => {});
    }
    if (activeTab === 'bookings') {
      setAdminBookingsLoading(true);
      apiService.getAdminAllBookings()
        .then(res => { if (res.success) setAdminBookings(res.data); })
        .catch(err => toast(err.message || 'Failed to load bookings', 'error'))
        .finally(() => setAdminBookingsLoading(false));
    }
    if (activeTab === 'requirements') {
      setAdminReqLoading(true);
      apiService.getAdminRequirements()
        .then(res => { if (res.success) setAdminRequirements(res.data); })
        .catch(err => toast(err.message || 'Failed to load requirements', 'error'))
        .finally(() => setAdminReqLoading(false));
    }
    if (activeTab === 'catalogue') {
      setCatalogueLoading(true);
      apiService.getExperts()
        .then(res => {
          if (res.success) {
            const dummyExperts = res.data.filter(e => e.isDummy);
            setCatalogueExperts(dummyExperts);
          }
        })
        .catch(err => toast(err.message || 'Failed to load catalogue', 'error'))
        .finally(() => setCatalogueLoading(false));
    }
  }, [activeTab]);

  React.useEffect(() => {
    if (activeTab === 'languages' || activeTab === 'categories') {
      fetchLanguages();
      fetchCategories();
    }
    if (activeTab === 'tags') {
      fetchTags();
    }
    if (activeTab === 'settings') {
      // Load customer care numbers
      fetch(`${envConfig.apiBaseUrl}/content/customer-care`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data.length > 0) {
            setAdminCustomerCareNumbers(data.data.map(num => ({
              country: num.country,
              phone: num.phone,
              whatsapp: num.whatsapp || ''
            })));
          }
        })
        .catch(err => console.error('Failed to load customer care numbers:', err));
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const [expertsRes, learnersRes] = await Promise.all([
        apiService.getUsersByRole('expert'),
        apiService.getUsersByRole('learner'),
      ]);

      if (expertsRes.success) setExperts(expertsRes.data || []);
      if (learnersRes.success) setLearners(learnersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await apiService.deleteUser(userId);

      if (result.success) {
        toast('User deleted successfully', 'success');
        setShowUserDetails(false);
        fetchUsers(); // Refresh the list
      } else {
        throw new Error(result.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast(error.message || 'Failed to delete user', 'error');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast('Password must be at least 6 characters long', 'error');
      return;
    }

    try {
      const result = await apiService.changeUserPassword(selectedUser.id, newPassword);

      if (result.success) {
        toast('Password changed successfully', 'success');
        setNewPassword('');
        setShowPasswordChange(false);
      } else {
        throw new Error(result.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      toast('Failed to change password', 'error');
    }
  };

  const handleLogout = () => {
    logout();
    toast('Logged out successfully', 'success');
    window.location.href = '/admin'; // Redirect to admin login
  };

  const downloadExcel = (data, filename) => {
    // Convert data to CSV format
    const headers = ['Name', 'Email', 'Mobile', 'City', 'Role', 'Status', 'Joined'];
    const csvContent = [
      headers.join(','),
      ...data.map(u => [
        `"${u.name}"`,
        u.email,
        u.mobile || '-',
        `"${u.city || '-'}"`,
        u.role,
        u.status,
        u.joined
      ].join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast(`Downloaded ${filename}`, 'success');
  };

  const downloadExperts = () => downloadExcel(experts, 'experts.csv');
  const downloadLearners = () => downloadExcel(learners, 'learners.csv');

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`${newStatus === 'inactive' ? 'Deactivate' : 'Activate'} ${u.name}? ${newStatus === 'inactive' ? 'They will be unable to log in.' : ''}`)) return;
    try {
      const res = await apiService.toggleUserStatus(u.id, newStatus);
      if (!res.success) throw new Error(res.message);
      toast(`${u.name} is now ${newStatus}`, 'success');
      const updater = prev => prev.map(x => x.id === u.id ? { ...x, status: newStatus } : x);
      if (u.role === 'expert') setExperts(updater);
      else setLearners(updater);
      if (selectedUser?.id === u.id) setSelectedUser(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      toast(err.message || 'Failed to update status', 'error');
    }
  };

  const downloadBookingsExcel = () => {
    const headers = ['ID','Learner','Expert','Service','Date','Time','Amount','Payment','Status'];
    const rows = adminBookings.map(b => [
      b.id,
      `"${b.learner_name || ''}"`,
      `"${b.expert_name || ''}"`,
      `"${(b.service || '').replace(/"/g, '')}"`,
      b.slot?.date || '',
      b.slot?.start || '',
      b.price || 0,
      b.payment_status || '',
      b.status || '',
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'bookings.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast('Downloaded bookings.csv', 'success');
  };

  const downloadEarningsExcel = () => {
    const headers = ['Expert','Email','City','Sessions','Gross Revenue','Commission %','Commission (₹)','Net Earnings','Payouts Requested','Pending Balance'];
    const rows = earningsData.map(e => [
      `"${e.expertName}"`,
      e.email,
      `"${e.city || ''}"`,
      e.totalSessions,
      e.grossRevenue,
      `${e.commissionRate ?? 15}%`,
      e.platformCommission,
      e.expertEarnings,
      e.totalPayoutsRequested,
      Math.max(0, e.pendingBalance),
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'earnings.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast('Downloaded earnings.csv', 'success');
  };

  const handleTabClick = (tabId) => {
    if (tabId === 'logout') {
      localStorage.removeItem('admin_tab');
      handleLogout();
    } else {
      localStorage.setItem('admin_tab', tabId);
      setActiveTab(tabId);
    }
  };

  const fetchLanguages = async () => {
    setLoadingContent(true);
    try {
      const res = await apiService.getLanguages();
      if (res.success) setLanguages(res.data);
    } catch (err) {
      toast('Failed to load languages', 'error');
    } finally {
      setLoadingContent(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingContent(true);
    try {
      const res = await apiService.getCategories();
      if (res.success) setCatData(res.data);
    } catch (err) {
      toast('Failed to load categories', 'error');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleEditLang = (lang) => {
    setEditingLang(lang);
    setLangForm({ name: lang.name, code: lang.code });
    setShowLangModal(true);
  };

  const handleSaveLang = async () => {
    if (!langForm.name || !langForm.code) { toast('Name and code are required', 'error'); return; }
    try {
      if (editingLang) {
        await apiService.updateLanguage(editingLang.id, { name: langForm.name, code: langForm.code });
        toast('Language updated ✓', 'success');
      } else {
        await apiService.createLanguage(langForm.name, langForm.code);
        toast('Language added ✓', 'success');
      }
      setShowLangModal(false);
      setLangForm({ name:'', code:'' });
      setEditingLang(null);
      fetchLanguages();
    } catch (err) {
      toast(err.message || 'Operation failed', 'error');
    }
  };

  const handleDeleteLang = async (id) => {
    if (!window.confirm('Delete this language? This cannot be undone.')) return;
    try {
      await apiService.deleteLanguage(id);
      toast('Language deleted', 'success');
      fetchLanguages();
    } catch (err) {
      toast(err.message || 'Failed to delete', 'error');
    }
  };

  const handleToggleLanguage = async (id, enabled) => {
    try {
      await apiService.updateLanguage(id, { enabled });
      fetchLanguages();
    } catch (err) {
      toast('Failed to update language', 'error');
    }
  };

  const handleEditCat = (cat) => {
    setEditingCat(cat);
    setCatModalForm({ name: cat.name, icon: cat.icon });
    setShowCatModal(true);
  };

  const handleSaveCat = async () => {
    if (!catModalForm.name || !catModalForm.icon) {
      toast('Name and icon are required', 'error'); return;
    }
    try {
      if (editingCat) {
        await apiService.updateCategory(editingCat.id, catModalForm);
        toast('Category updated ✓', 'success');
      } else {
        await apiService.createCategory(catModalForm);
        toast('Category added ✓', 'success');
      }
      setShowCatModal(false);
      setCatModalForm({ name:'', icon:'' });
      setEditingCat(null);
      fetchCategories();
    } catch (err) {
      toast(err.message || 'Operation failed', 'error');
    }
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm('Delete this category and all its subcategories? This cannot be undone.')) return;
    try {
      await apiService.deleteCategory(id);
      toast('Category deleted', 'success');
      if (expandedCat === id) setExpandedCat(null);
      fetchCategories();
    } catch (err) {
      toast(err.message || 'Failed to delete', 'error');
    }
  };

  const handleToggleCat = async (id, enabled) => {
    try {
      await apiService.updateCategory(id, { enabled });
      fetchCategories();
    } catch (err) {
      toast('Failed to update category', 'error');
    }
  };

  const handleSaveSub = async () => {
    if (!subForm.name) { toast('Subcategory name is required', 'error'); return; }
    try {
      if (editingSub) {
        await apiService.updateSubcategory(editingSub.id, { name: subForm.name });
        toast('Subcategory updated ✓', 'success');
      } else {
        await apiService.createSubcategory(subModalCatId, subForm.name);
        toast('Subcategory added ✓', 'success');
      }
      setShowSubModal(false);
      setSubForm({ name:'' });
      setEditingSub(null);
      fetchCategories();
    } catch (err) {
      toast(err.message || 'Operation failed', 'error');
    }
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    try {
      await apiService.deleteSubcategory(id);
      toast('Subcategory deleted', 'success');
      fetchCategories();
    } catch (err) {
      toast(err.message || 'Failed to delete', 'error');
    }
  };

  const handleToggleSub = async (id, enabled) => {
    try {
      await apiService.updateSubcategory(id, { enabled });
      fetchCategories();
    } catch (err) {
      toast('Failed to update subcategory', 'error');
    }
  };

  const fetchTags = async () => {
    setLoadingContent(true);
    try {
      const res = await apiService.getTags();
      if (res.success) setTagData(res.data);
    } catch (err) {
      toast('Failed to load tags', 'error');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagForm({ name: tag.name });
    setShowTagModal(true);
  };

  const handleSaveTag = async () => {
    if (!tagForm.name.trim()) { toast('Tag name is required', 'error'); return; }
    try {
      if (editingTag) {
        await apiService.updateTag(editingTag.id, { name: tagForm.name });
        toast('Tag updated ✓', 'success');
      } else {
        await apiService.createTag(tagForm.name);
        toast('Tag added ✓', 'success');
      }
      setShowTagModal(false);
      setTagForm({ name:'' });
      setEditingTag(null);
      fetchTags();
    } catch (err) {
      toast(err.message || 'Operation failed', 'error');
    }
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm('Delete this tag?')) return;
    try {
      await apiService.deleteTag(id);
      toast('Tag deleted', 'success');
      fetchTags();
    } catch (err) {
      toast(err.message || 'Failed to delete', 'error');
    }
  };

  const handleToggleTag = async (id, enabled) => {
    try {
      await apiService.updateTag(id, { enabled });
      fetchTags();
    } catch (err) {
      toast('Failed to update tag', 'error');
    }
  };

  // Admin Settings Handlers
  const handleAdminPasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = adminPasswordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast('All password fields are required', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast('Passwords do not match', 'error');
      return;
    }

    if (newPassword.length < 6) {
      toast('Password must be at least 6 characters', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${envConfig.apiBaseUrl}/auth/profile/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();
      if (data.success) {
        toast('Password changed successfully', 'success');
        setAdminPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast(data.message || 'Failed to change password', 'error');
      }
    } catch (err) {
      toast('Error changing password: ' + err.message, 'error');
    }
  };

  const handleAdminSecurityUpdate = async () => {
    const { question, answer } = adminSecurityForm;

    if (!question || !answer) {
      toast('Please select a question and provide an answer', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${envConfig.apiBaseUrl}/auth/update-security-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ securityQuestion: question, securityAnswer: answer })
      });

      const data = await response.json();
      if (data.success) {
        toast('Security question updated successfully', 'success');
        setAdminSecurityForm({ question: '', answer: '' });
      } else {
        toast(data.message || 'Failed to update security question', 'error');
      }
    } catch (err) {
      toast('Error updating security question: ' + err.message, 'error');
    }
  };

  const handleSaveCustomerCareNumbers = async () => {
    // Validate
    if (adminCustomerCareNumbers.some(num => !num.country || (!num.phone && !num.whatsapp))) {
      toast('Please fill in all country fields and provide at least one phone or WhatsApp contact per entry', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${envConfig.apiBaseUrl}/content/customer-care`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ numbers: adminCustomerCareNumbers })
      });

      const data = await response.json();
      if (data.success) {
        toast('Customer care numbers saved successfully', 'success');
      } else {
        toast(data.message || 'Failed to save customer care numbers', 'error');
      }
    } catch (err) {
      toast('Error saving customer care numbers: ' + err.message, 'error');
    }
  };

  return (
    <div className="admin-layout" style={{ display:'flex', minHeight:'calc(100vh - 60px)' }}>
      <div className="admin-sidebar">
        <div className="admin-sidebar-header" style={{ color:'var(--saffron)',fontFamily:'var(--font-display)',fontSize:16,fontWeight:700,padding:'16px 20px 16px',borderBottom:'1px solid #2C2218',marginBottom:8 }}>⚙ Admin Panel</div>
        {SIDEBAR.map(({ id, label, icon }) => (
          <div 
            key={id} 
            className={`admin-sidebar-item ${activeTab === id ? 'active' : ''}`} 
            onClick={() => handleTabClick(id)}
            style={id === 'logout' ? { color: '#C13030' } : {}}
          >
            <span>{icon}</span> {label}
          </div>
        ))}
      </div>

      <div style={{ flex:1, background:'var(--bg)', overflow:'auto', display:'flex', flexDirection:'column' }}>
        
        {/* Admin Header with Logout */}
        <div className="admin-header" style={{ 
          display:'flex', 
          justifyContent:'space-between', 
          alignItems:'center',
          padding:'16px 28px',
          borderBottom:'1px solid var(--border)',
          background:'var(--surface)',
          position:'sticky',
          top:0,
          zIndex:10
        }}>
          <div>
            <h1 style={{ fontSize:20, fontWeight:700, color:'var(--ink)', margin:0 }}>
              {activeTab === 'dashboard' && 'Dashboard'}
              {activeTab === 'languages' && 'Language Manager'}
              {activeTab === 'categories' && 'Category Manager'}
              {activeTab === 'pricing' && 'Price Enumeration'}
              {activeTab === 'experts' && 'Experts Management'}
              {activeTab === 'learners' && 'Learners Management'}
              {activeTab === 'tags' && 'Tag Manager'}
              {activeTab === 'catalogue' && 'Catalogue'}
              {activeTab === 'bookings' && 'All Bookings'}
              {activeTab === 'requirements' && 'Requirements'}
              {activeTab === 'earnings' && 'Earnings'}
              {activeTab === 'settings' && 'Admin Settings'}
            </h1>
          </div>
          <button 
            onClick={handleLogout}
            className="btn btn-secondary"
            style={{ 
              display:'flex', 
              alignItems:'center', 
              gap:8,
              background:'#C13030',
              color:'white',
              border:'none'
            }}
          >
            🚪 Logout
          </button>
        </div>

        {/* Main Content */}
        <div className="admin-content" style={{ flex:1, padding:28, overflow:'auto' }}>

        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Platform Overview" subtitle="MakeMyExpert admin dashboard" />
            <div className="grid-4 mb-24">
              <div className="stat-card">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{adminStats ? adminStats.totalUsers.toLocaleString() : '—'}</div>
                <div className="stat-delta">{adminStats ? `${adminStats.totalExperts} experts · ${adminStats.totalLearners} learners` : 'Loading…'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Active Experts</div>
                <div className="stat-value">{adminStats ? adminStats.activeExperts.toLocaleString() : '—'}</div>
                <div className="stat-delta">{adminStats ? `of ${adminStats.totalExperts} total experts` : 'Loading…'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Sessions This Month</div>
                <div className="stat-value">{adminStats ? adminStats.sessionsThisMonth.toLocaleString() : '—'}</div>
                <div className="stat-delta">Bookings since month start</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Platform Revenue</div>
                <div className="stat-value">{adminStats ? '₹' + adminStats.platformRevenue.toLocaleString() : '—'}</div>
                <div className="stat-delta">Commission on paid sessions (per-expert rate)</div>
              </div>
            </div>
            <div className="grid-2">
              <div className="card card-body">
                <h3 style={{ fontWeight:700, marginBottom:16 }}>Top Categories by Experts</h3>
                {(() => {
                  const top = categories.slice(0, 6);
                  const maxExp = Math.max(...top.map(c => c.experts || 0), 1);
                  return top.map(c => (
                    <div key={c.id} className="flex items-center gap-12 mb-10">
                      <span style={{ fontSize:18 }}>{c.icon}</span>
                      <span style={{ fontSize:13, minWidth:90 }}>{c.name}</span>
                      <div style={{ flex:1 }}>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{ width:`${Math.round(((c.experts||0)/maxExp)*100)}%`, background:'var(--saffron)' }} />
                        </div>
                      </div>
                      <span style={{ fontSize:12, fontWeight:600, color:'var(--ink3)', minWidth:30, textAlign:'right' }}>{c.experts || 0}</span>
                    </div>
                  ));
                })()}
              </div>
              <div className="card card-body">
                <h3 style={{ fontWeight:700, marginBottom:16 }}>City Distribution</h3>
                {adminStats?.cityDistribution?.length > 0
                  ? adminStats.cityDistribution.map(({ city, pct }) => (
                    <div key={city} className="flex items-center gap-12 mb-10">
                      <span style={{ fontSize:13, minWidth:90, color:'var(--ink2)' }}>{city}</span>
                      <div style={{ flex:1 }}>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{ width:`${pct}%`, background:'var(--teal)' }} />
                        </div>
                      </div>
                      <span style={{ fontSize:12, fontWeight:600, color:'var(--ink3)', minWidth:30, textAlign:'right' }}>{pct}%</span>
                    </div>
                  ))
                  : <p style={{ fontSize:13, color:'var(--ink4)' }}>No city data available yet.</p>
                }
              </div>
            </div>
          </div>
        )}

        {/* Languages */}
        {activeTab === 'languages' && (
          <div className="animate-fadeUp">
            <SectionHeader
              title="Language Manager"
              subtitle="Manage languages available on the platform."
              right={<button onClick={() => { setEditingLang(null); setLangForm({name:'', code:''}); setShowLangModal(true); }} className="btn btn-primary btn-sm">+ New Language</button>}
            />
            {loadingContent ? (
              <div style={{ textAlign:'center', padding:40 }}><div style={{ fontSize:24, marginBottom:16 }}>⏳</div><div>Loading…</div></div>
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Name</th><th>Code</th><th>Enabled</th><th>Actions</th></tr></thead>
                  <tbody>
                    {languages.length === 0 ? (
                      <tr><td colSpan="4" style={{ textAlign:'center', padding:40, color:'var(--ink4)' }}>No languages added yet</td></tr>
                    ) : languages.map(lang => (
                      <tr key={lang.id}>
                        <td style={{ fontWeight:600 }}>{lang.name}</td>
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--ink3)' }}>{lang.code}</td>
                        <td><EnableToggle enabled={!!lang.enabled} onChange={v => handleToggleLanguage(lang.id, v)} /></td>
                        <td>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={() => handleEditLang(lang)} className="btn btn-secondary btn-sm" style={{ fontSize:12 }}>Edit</button>
                            <button onClick={() => handleDeleteLang(lang.id)} className="btn btn-danger btn-sm" style={{ fontSize:12 }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Categories */}
        {activeTab === 'categories' && (
          <div className="animate-fadeUp">
            <SectionHeader
              title="Category Manager"
              subtitle="Manage service categories and their subcategories."
              right={<button onClick={() => { setEditingCat(null); setCatModalForm({name:'', icon:''}); setShowCatModal(true); }} className="btn btn-primary btn-sm">+ New Category</button>}
            />
            {loadingContent ? (
              <div style={{ textAlign:'center', padding:40 }}><div style={{ fontSize:24, marginBottom:16 }}>⏳</div><div>Loading…</div></div>
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Icon</th><th>Category</th><th>Subcategories</th><th>Enabled</th><th>Actions</th></tr></thead>
                  <tbody>
                    {catData.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign:'center', padding:40, color:'var(--ink4)' }}>No categories added yet</td></tr>
                    ) : catData.map(cat => (
                      <React.Fragment key={cat.id}>
                        <tr>
                          <td style={{ fontSize:22 }}>{cat.icon}</td>
                          <td style={{ fontWeight:600 }}>{cat.name}</td>
                          <td><span style={{ fontSize:12, color:'var(--ink3)' }}>{(cat.subcategories||[]).length} subs</span></td>
                          <td><EnableToggle enabled={!!cat.enabled} onChange={v => handleToggleCat(cat.id, v)} /></td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={() => handleEditCat(cat)} className="btn btn-secondary btn-sm" style={{ fontSize:11 }}>Edit</button>
                              <button
                                onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize:11, ...(expandedCat === cat.id ? { background:'var(--teal)', color:'white' } : {}) }}
                              >{expandedCat === cat.id ? 'Hide Subs' : 'Subs ▾'}</button>
                              <button onClick={() => handleDeleteCat(cat.id)} className="btn btn-danger btn-sm" style={{ fontSize:11 }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                        {expandedCat === cat.id && (
                          <tr>
                            <td colSpan="5" style={{ padding:0, background:'var(--bg)' }}>
                              <div style={{ margin:'0 8px 10px 52px', padding:'14px 18px', background:'var(--surface)', borderLeft:'3px solid var(--teal)', borderRadius:'0 8px 8px 0' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                                  <span style={{ fontWeight:700, fontSize:13 }}>Subcategories — {cat.name}</span>
                                  <button
                                    onClick={() => { setSubModalCatId(cat.id); setEditingSub(null); setSubForm({name:''}); setShowSubModal(true); }}
                                    className="btn btn-primary btn-sm"
                                    style={{ fontSize:11 }}
                                  >+ Add Subcategory</button>
                                </div>
                                {(cat.subcategories||[]).length === 0 ? (
                                  <p style={{ color:'var(--ink4)', fontSize:13, margin:0 }}>No subcategories yet.</p>
                                ) : (
                                  <table className="data-table" style={{ fontSize:13 }}>
                                    <thead><tr><th>Name</th><th>Enabled</th><th>Actions</th></tr></thead>
                                    <tbody>
                                      {(cat.subcategories||[]).map(sub => (
                                        <tr key={sub.id}>
                                          <td style={{ fontWeight:500 }}>{sub.name}</td>
                                          <td><EnableToggle enabled={!!sub.enabled} onChange={v => handleToggleSub(sub.id, v)} /></td>
                                          <td>
                                            <div style={{ display:'flex', gap:6 }}>
                                              <button onClick={() => { setSubModalCatId(cat.id); setEditingSub(sub); setSubForm({name:sub.name}); setShowSubModal(true); }} className="btn btn-secondary btn-sm" style={{ fontSize:11 }}>Edit</button>
                                              <button onClick={() => handleDeleteSub(sub.id)} className="btn btn-danger btn-sm" style={{ fontSize:11 }}>Delete</button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pricing */}
        {activeTab === 'pricing' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Price Enumeration" subtitle="Set allowed price bands per category and quality tier. Experts must price within these ranges." />
            {categories.map(c => {
              const tiers = priceTiers[c.id] || { standard:{min:300,max:800}, premium:{min:800,max:2000}, elite:{min:2000,max:4000} };
              return (
                <div key={c.id} className="card card-body mb-16">
                  <div className="flex items-center gap-12 mb-16">
                    <span style={{ fontSize:22 }}>{c.icon}</span>
                    <h3 style={{ fontWeight:700 }}>{c.name}</h3>
                    {!c.enabled && <span className="badge badge-gray">Disabled</span>}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                    {[['standard','🥉 Standard'],['premium','🥈 Premium'],['elite','🥇 Elite']].map(([tier, label]) => (
                      <div key={tier} style={{ border:`1.5px solid ${tier==='elite'?'var(--teal)':tier==='premium'?'var(--saffron)':'var(--border)'}`, borderRadius:10, padding:16, background: tier==='elite'?'var(--teal-lt)':tier==='premium'?'var(--saffron-lt)':'var(--surface)' }}>
                        <div style={{ fontWeight:700, marginBottom:12 }}>{label}</div>
                        {['min','max'].map(mm => (
                          <div key={mm} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, fontSize:13 }}>
                            <span style={{ color:'var(--ink3)' }}>{mm === 'min' ? 'Min (₹)' : 'Max (₹)'}</span>
                            <input type="number" defaultValue={tiers[tier]?.[mm] || 0}
                              onBlur={e => { updatePriceTier(c.id, tier, mm, e.target.value); toast('Price range updated ✓'); }}
                              style={{ width:90, padding:'4px 8px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, textAlign:'right', fontFamily:'var(--font-mono)' }} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Experts */}
        {activeTab === 'experts' && (
          <div className="animate-fadeUp">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <SectionHeader title="Expert Management" subtitle="Manage platform experts" />
              <button 
                onClick={downloadExperts} 
                className="btn btn-primary btn-sm"
                disabled={experts.length === 0}
                style={{ gap: 6 }}
              >
                📥 Download Excel
              </button>
            </div>
            
            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 24, marginBottom: 16 }}>⏳</div>
                <div>Loading experts...</div>
              </div>
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>City</th>
                      <th>Joined</th>
                      <th>Commission %</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {experts.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: 40, color: 'var(--ink4)' }}>
                          No experts registered yet
                        </td>
                      </tr>
                    ) : (
                      experts.map(u => (
                        <tr key={u.id}>
                          <td style={{ fontWeight:600 }}>{u.name}</td>
                          <td style={{ fontSize: 12, color: 'var(--ink4)' }}>{u.email}</td>
                          <td style={{ fontSize: 12, color: 'var(--ink4)' }}>{u.mobile}</td>
                          <td>{u.city || '-'}</td>
                          <td style={{ color:'var(--ink4)' }}>{u.joined}</td>
                          <td>
                            {editingCommission?.expertId === u.id ? (
                              <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                                <input
                                  type="number" min="0" max="100" step="0.5"
                                  value={editingCommission.value}
                                  onChange={e => setEditingCommission(c => ({ ...c, value: e.target.value }))}
                                  style={{ width:58, border:'1.5px solid var(--teal)', borderRadius:6, padding:'3px 6px', fontSize:13, outline:'none' }}
                                  onKeyDown={e => { if (e.key === 'Escape') setEditingCommission(null); }}
                                />
                                <span style={{ fontSize:12, color:'var(--ink3)' }}>%</span>
                                <button
                                  disabled={commissionSaving}
                                  className="btn btn-teal btn-sm"
                                  style={{ fontSize:11, padding:'2px 8px' }}
                                  onClick={async () => {
                                    const rate = parseFloat(editingCommission.value);
                                    if (isNaN(rate) || rate < 0 || rate > 100) {
                                      toast('Enter a value between 0 and 100', 'error'); return;
                                    }
                                    setCommissionSaving(true);
                                    try {
                                      await apiService.updateExpertCommission(u.id, rate);
                                      setExperts(prev => prev.map(e => e.id === u.id ? { ...e, commissionRate: rate } : e));
                                      setEditingCommission(null);
                                      toast(`Commission for ${u.name.split(' ')[0]} updated to ${rate}%`, 'success');
                                    } catch (err) {
                                      toast(err.message || 'Failed to update', 'error');
                                    } finally { setCommissionSaving(false); }
                                  }}
                                >✓</button>
                                <button className="btn btn-secondary btn-sm" style={{ fontSize:11, padding:'2px 8px' }} onClick={() => setEditingCommission(null)}>✕</button>
                              </div>
                            ) : (
                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <span style={{ fontWeight:700, color: (u.commissionRate ?? 15) === 15 ? 'var(--ink3)' : 'var(--saffron)' }}>
                                  {u.commissionRate ?? 15}%
                                </span>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  style={{ fontSize:10, padding:'2px 7px' }}
                                  onClick={() => setEditingCommission({ expertId: u.id, value: String(u.commissionRate ?? 15) })}
                                >Edit</button>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="badge" style={{
                              background: u.status === 'active' ? '#dcfce7' : '#fee2e2',
                              color: u.status === 'active' ? '#15803d' : '#dc2626',
                            }}>{u.status}</span>
                          </td>
                          <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <button onClick={() => handleUserDetails(u)} className="btn btn-secondary btn-sm" style={{ fontSize: 12 }}>Details</button>
                            <button
                              onClick={() => handleToggleStatus(u)}
                              className="btn btn-sm"
                              style={{
                                fontSize: 11,
                                background: u.status === 'active' ? '#FEF3C7' : '#DCFCE7',
                                color: u.status === 'active' ? '#B45309' : '#15803D',
                                border: '1px solid currentColor',
                              }}
                            >
                              {u.status === 'active' ? '⏸ Deactivate' : '▶ Activate'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Learners */}
        {activeTab === 'learners' && (
          <div className="animate-fadeUp">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <SectionHeader title="Learner Management" subtitle="Manage platform learners" />
              <button 
                onClick={downloadLearners} 
                className="btn btn-primary btn-sm"
                disabled={learners.length === 0}
                style={{ gap: 6 }}
              >
                📥 Download Excel
              </button>
            </div>
            
            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 24, marginBottom: 16 }}>⏳</div>
                <div>Loading learners...</div>
              </div>
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>City</th>
                      <th>Joined</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learners.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: 40, color: 'var(--ink4)' }}>
                          No learners registered yet
                        </td>
                      </tr>
                    ) : (
                      learners.map(u => (
                        <tr key={u.id}>
                          <td style={{ fontWeight:600 }}>{u.name}</td>
                          <td style={{ fontSize: 12, color: 'var(--ink4)' }}>{u.email}</td>
                          <td style={{ fontSize: 12, color: 'var(--ink4)' }}>{u.mobile}</td>
                          <td>{u.city || '-'}</td>
                          <td style={{ color:'var(--ink4)' }}>{u.joined}</td>
                          <td>
                            <span className="badge" style={{
                              background: u.status === 'active' ? '#dcfce7' : '#fee2e2',
                              color: u.status === 'active' ? '#15803d' : '#dc2626',
                            }}>{u.status}</span>
                          </td>
                          <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <button onClick={() => handleUserDetails(u)} className="btn btn-secondary btn-sm" style={{ fontSize: 12 }}>Details</button>
                            <button
                              onClick={() => handleToggleStatus(u)}
                              className="btn btn-sm"
                              style={{
                                fontSize: 11,
                                background: u.status === 'active' ? '#FEF3C7' : '#DCFCE7',
                                color: u.status === 'active' ? '#B45309' : '#15803D',
                                border: '1px solid currentColor',
                              }}
                            >
                              {u.status === 'active' ? '⏸ Deactivate' : '▶ Activate'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        {activeTab === 'tags' && (
          <div className="animate-fadeUp">
            <SectionHeader
              title="Tag Manager"
              subtitle="Tags that experts can select when adding their services"
              right={<button onClick={() => { setEditingTag(null); setTagForm({name:''}); setShowTagModal(true); }} className="btn btn-primary btn-sm">+ New Tag</button>}
            />
            {loadingContent ? (
              <div style={{ textAlign:'center', padding:40, color:'var(--ink4)' }}>Loading…</div>
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Tag Name</th><th style={{ textAlign:'center' }}>Enabled</th><th style={{ textAlign:'right' }}>Actions</th></tr>
                  </thead>
                  <tbody>
                    {tagData.length === 0 && (
                      <tr><td colSpan={3} style={{ textAlign:'center', color:'var(--ink4)', padding:32 }}>No tags yet. Add your first tag.</td></tr>
                    )}
                    {tagData.map(tag => (
                      <tr key={tag.id}>
                        <td>
                          <span style={{ display:'inline-block', background:'var(--bg)', border:'1px solid var(--border2)', borderRadius:20, padding:'3px 12px', fontSize:13, fontWeight:500 }}>
                            🏷️ {tag.name}
                          </span>
                        </td>
                        <td><EnableToggle enabled={!!tag.enabled} onChange={v => handleToggleTag(tag.id, v)} /></td>
                        <td style={{ textAlign:'right' }}>
                          <button onClick={() => handleEditTag(tag)} className="btn btn-secondary btn-sm" style={{ marginRight:6 }}>Edit</button>
                          <button onClick={() => handleDeleteTag(tag.id)} className="btn btn-sm" style={{ background:'#FEE2E2', color:'#C13030', border:'none' }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Catalogue - Dummy Experts */}
        {activeTab === 'catalogue' && (
          <div className="animate-fadeUp">
            <SectionHeader
              title="Catalogue"
              subtitle="Manage dummy expert profiles to build customer confidence"
            />
            {catalogueLoading ? (
              <div style={{ textAlign:'center', padding:40, color:'var(--ink4)' }}>Loading catalogue…</div>
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Category</th><th>City</th><th>Experience</th><th>Price</th><th>Slots</th><th style={{ textAlign:'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogueExperts.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--ink4)', padding:32 }}>No dummy experts in catalogue</td></tr>
                    )}
                    {catalogueExperts.map(expert => (
                      <tr key={expert.id}>
                        <td><strong>{expert.name}</strong></td>
                        <td>{expert.catName || '—'}</td>
                        <td>{expert.city}</td>
                        <td>{expert.experience ? `${expert.experience}+ yrs` : '—'}</td>
                        <td>{expert.rate ? `₹${expert.rate}` : '—'}</td>
                        <td>{(expert.slots || []).length}</td>
                        <td style={{ textAlign:'right' }}>
                          <button
                            onClick={() => {
                              if (expert.id) {
                                navigate(`/expert/${expert.id}`);
                              } else {
                                console.error('Expert ID missing:', expert);
                                toast('Error: Expert ID not found', 'error');
                              }
                            }}
                            className="btn btn-secondary btn-sm"
                            style={{ marginRight:6 }}
                          >
                            View
                          </button>
                          <button onClick={() => {}} className="btn btn-sm" style={{ background:'#FEE2E2', color:'#C13030', border:'none' }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Bookings */}
        {activeTab === 'bookings' && (() => {
          const FILTERS = ['all', 'pending', 'confirmed', 'completed', 'declined', 'cancelled'];
          const counts = FILTERS.reduce((acc, f) => {
            acc[f] = f === 'all' ? adminBookings.length : adminBookings.filter(b => b.status === f).length;
            return acc;
          }, {});
          const filtered = adminBookingFilter === 'all'
            ? adminBookings
            : adminBookings.filter(b => b.status === adminBookingFilter);

          return (
            <div className="animate-fadeUp">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <SectionHeader title="All Bookings" subtitle={adminBookingsLoading ? 'Loading…' : `${adminBookings.length} total bookings`} />
                <button onClick={downloadBookingsExcel} disabled={adminBookings.length === 0} className="btn btn-primary btn-sm" style={{ gap:6 }}>
                  📥 Download Excel
                </button>
              </div>

              {/* Filter tabs */}
              <div className="tabs-row" style={{ marginBottom: 16 }}>
                {FILTERS.map(f => (
                  <div
                    key={f}
                    className={`tab-item ${adminBookingFilter === f ? 'active' : ''}`}
                    onClick={() => setAdminBookingFilter(f)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                    {counts[f] > 0 && (
                      <span style={{
                        background: f === 'declined' || f === 'cancelled'
                          ? '#fee2e2' : f === 'pending'
                          ? 'var(--saffron-lt)' : 'var(--teal-lt)',
                        color: f === 'declined' || f === 'cancelled'
                          ? '#dc2626' : f === 'pending'
                          ? 'var(--saffron)' : 'var(--teal)',
                        fontSize: 11, fontWeight: 700,
                        padding: '1px 7px', borderRadius: 10,
                      }}>
                        {counts[f]}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {adminBookingsLoading ? (
                <div style={{ textAlign:'center', padding:40 }}><div style={{ fontSize:24, marginBottom:12 }}>⏳</div><div>Loading bookings…</div></div>
              ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th><th>Learner</th><th>Expert</th><th>Service</th>
                      <th>Date / Time</th><th>Amount</th><th>Payment</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign:'center', padding: 32, color:'var(--ink4)' }}>
                        No {adminBookingFilter === 'all' ? '' : adminBookingFilter} bookings found
                      </td></tr>
                    ) : filtered.map(b => (
                      <tr key={b.id}>
                        <td style={{ fontFamily:'var(--font-mono)',fontSize:11,color:'var(--ink4)' }}>#{b.id}</td>
                        <td>{b.learner_name || b.receiverId}</td>
                        <td style={{ fontWeight:600 }}>{b.expert_name || b.expertId}</td>
                        <td style={{ maxWidth: 140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.service}</td>
                        <td style={{ fontFamily:'var(--font-mono)',fontSize:12 }}>{b.slot?.date} {b.slot?.start}</td>
                        <td style={{ fontWeight:700,color:'var(--saffron)' }}>₹{b.price?.toLocaleString()}</td>
                        <td>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                            background: b.payment_status === 'paid' ? '#dcfce7' : '#f3f4f6',
                            color: b.payment_status === 'paid' ? '#16a34a' : 'var(--ink3)',
                          }}>
                            {b.payment_status === 'paid' ? '✓ Paid' : 'Unpaid'}
                          </span>
                        </td>
                        <td><BookingBadge status={b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'requirements' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Learner Requirements" subtitle="Requirements posted by learners looking for experts." />
            {adminReqLoading ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--ink3)' }}>Loading…</div>
            ) : adminRequirements.length === 0 ? (
              <EmptyState icon="📩" title="No requirements yet" description="Learner requirements will appear here." />
            ) : (
              <div className="card admin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Requirement</th>
                      <th>Contact</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminRequirements.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight:600, whiteSpace:'nowrap' }}>{r.learner_name}</td>
                        <td>
                          <div style={{ fontWeight:600, fontSize:13 }}>{r.title}</div>
                          {r.description && <div style={{ fontSize:12, color:'var(--ink4)', marginTop:2 }}>{r.description}</div>}
                        </td>
                        <td style={{ fontSize:12 }}>
                          {r.email && <div>{r.email}</div>}
                          {r.phone && <div style={{ color:'var(--ink3)' }}>{r.phone}</div>}
                        </td>
                        <td style={{ fontSize:12, color:'var(--ink3)', whiteSpace:'nowrap' }}>
                          {new Date(r.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                        </td>
                        <td>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background: r.status==='open'?'var(--teal)':'var(--border)', color: r.status==='open'?'white':'var(--ink4)' }}>
                            {r.status === 'open' ? '● Open' : '✓ Closed'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            {r.status === 'open' && (
                              <button
                                className="btn btn-teal btn-sm"
                                style={{ fontSize:11 }}
                                onClick={async () => {
                                  try {
                                    await apiService.adminCloseRequirement(r.id);
                                    setAdminRequirements(prev => prev.map(x => x.id === r.id ? { ...x, status:'closed' } : x));
                                    toast('Marked as closed', 'success');
                                  } catch (err) { toast(err.message || 'Failed', 'error'); }
                                }}
                              >Close</button>
                            )}
                            <button
                              className="btn btn-danger btn-sm"
                              style={{ fontSize:11 }}
                              onClick={async () => {
                                if (!window.confirm(`Delete requirement "${r.title}"?`)) return;
                                try {
                                  await apiService.adminDeleteRequirement(r.id);
                                  setAdminRequirements(prev => prev.filter(x => x.id !== r.id));
                                  toast('Deleted', 'success');
                                } catch (err) { toast(err.message || 'Failed', 'error'); }
                              }}
                            >Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="animate-fadeUp">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <SectionHeader title="Expert Earnings" subtitle="Net earnings = Gross Revenue − Platform Commission. Commission rate is set per expert." />
              <button onClick={downloadEarningsExcel} disabled={earningsData.length === 0} className="btn btn-primary btn-sm" style={{ gap:6 }}>
                📥 Download Excel
              </button>
            </div>
            {earningsLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)' }}>Loading earnings…</div>
            ) : earningsData.length === 0 ? (
              <EmptyState icon="💰" title="No earnings data yet" description="Earnings will appear here once experts complete paid sessions." />
            ) : (
              <>
                {/* Summary row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
                  {[
                    { label: 'Total Gross Revenue', value: '₹' + earningsData.reduce((s,e) => s + e.grossRevenue, 0).toLocaleString(), color: 'var(--saffron)' },
                    { label: 'Platform Commission', value: '₹' + earningsData.reduce((s,e) => s + e.platformCommission, 0).toLocaleString(), color: 'var(--teal)' },
                    { label: 'Expert Net Earnings', value: '₹' + earningsData.reduce((s,e) => s + e.expertEarnings, 0).toLocaleString(), color: '#4A7C59' },
                    { label: 'Payout Requests', value: '₹' + earningsData.reduce((s,e) => s + e.totalPayoutsRequested, 0).toLocaleString(), color: '#C13030' },
                  ].map(c => (
                    <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 6 }}>{c.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
                    </div>
                  ))}
                </div>

                {/* Per-expert table */}
                <div className="card admin-table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Expert</th>
                        <th>City</th>
                        <th style={{ textAlign: 'right' }}>Sessions</th>
                        <th style={{ textAlign: 'right' }}>Gross Revenue</th>
                        <th style={{ textAlign: 'right' }}>Commission %</th>
                        <th style={{ textAlign: 'right' }}>Commission (₹)</th>
                        <th style={{ textAlign: 'right' }}>Net Earnings</th>
                        <th style={{ textAlign: 'right' }}>Payouts Requested</th>
                        <th style={{ textAlign: 'right' }}>Pending Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earningsData.map(e => (
                        <tr key={e.expertId}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{e.expertName}</div>
                            <div style={{ fontSize: 11, color: 'var(--ink4)' }}>{e.email}</div>
                          </td>
                          <td style={{ color: 'var(--ink3)', fontSize: 12 }}>{e.city || '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{e.totalSessions}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--saffron)' }}>₹{e.grossRevenue.toLocaleString()}</td>
                          <td style={{ textAlign: 'right', color: (e.commissionRate ?? 15) !== 15 ? 'var(--saffron)' : 'var(--ink3)', fontWeight: 600 }}>{e.commissionRate ?? 15}%</td>
                          <td style={{ textAlign: 'right', color: 'var(--teal)', fontWeight: 600 }}>₹{e.platformCommission.toLocaleString()}</td>
                          <td style={{ textAlign: 'right', color: '#4A7C59', fontWeight: 700 }}>₹{e.expertEarnings.toLocaleString()}</td>
                          <td style={{ textAlign: 'right', color: '#C13030' }}>₹{e.totalPayoutsRequested.toLocaleString()}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: e.pendingBalance > 0 ? 'var(--ink)' : 'var(--ink4)' }}>
                            ₹{Math.max(0, e.pendingBalance).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Settings & Profile */}
        {activeTab === 'settings' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Admin Settings" subtitle="Manage your account, password, and customer care contacts" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {/* Left Column: Password & Security */}
              <div className="card card-body">
                <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Account Security</h3>

                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Enter current password"
                    value={adminPasswordForm.currentPassword}
                    onChange={e => setAdminPasswordForm(f => ({...f, currentPassword: e.target.value}))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Enter new password (min 6 chars)"
                    value={adminPasswordForm.newPassword}
                    onChange={e => setAdminPasswordForm(f => ({...f, newPassword: e.target.value}))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Confirm new password"
                    value={adminPasswordForm.confirmPassword}
                    onChange={e => setAdminPasswordForm(f => ({...f, confirmPassword: e.target.value}))}
                  />
                </div>

                <button
                  onClick={handleAdminPasswordChange}
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center' }}
                >
                  🔑 Change Password
                </button>

                <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

                <h4 style={{ fontWeight: 700, marginBottom: 12 }}>Security Question</h4>

                <div className="form-group">
                  <label className="form-label">Security Question</label>
                  <select
                    className="form-input"
                    value={adminSecurityForm.question}
                    onChange={e => setAdminSecurityForm(f => ({...f, question: e.target.value}))}
                  >
                    <option value="">Select a question...</option>
                    <option value="What is your mother's maiden name?">What is your mother's maiden name?</option>
                    <option value="What is the name of your first pet?">What is the name of your first pet?</option>
                    <option value="In what city were you born?">In what city were you born?</option>
                    <option value="What is your favorite movie?">What is your favorite movie?</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Your Answer</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Your answer to the security question"
                    value={adminSecurityForm.answer}
                    onChange={e => setAdminSecurityForm(f => ({...f, answer: e.target.value}))}
                  />
                </div>

                <button
                  onClick={handleAdminSecurityUpdate}
                  className="btn btn-secondary w-full"
                  style={{ justifyContent: 'center' }}
                >
                  💾 Update Security Question
                </button>
              </div>

              {/* Right Column: Customer Care */}
              <div className="card card-body">
                <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Customer Care Contacts</h3>
                <p style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 16 }}>Add or update customer care phone numbers and WhatsApp. These will be displayed in the Help section.</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                  {adminCustomerCareNumbers.map((num, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Country (e.g., India, USA)"
                        value={num.country}
                        onChange={e => {
                          const updated = [...adminCustomerCareNumbers];
                          updated[idx].country = e.target.value;
                          setAdminCustomerCareNumbers(updated);
                        }}
                        style={{ flex: '0 0 120px', minWidth: '100px' }}
                      />
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="Phone"
                        value={num.phone}
                        onChange={e => {
                          const updated = [...adminCustomerCareNumbers];
                          updated[idx].phone = e.target.value;
                          setAdminCustomerCareNumbers(updated);
                        }}
                        style={{ flex: '0 0 140px', minWidth: '100px' }}
                      />
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="WhatsApp"
                        value={num.whatsapp || ''}
                        onChange={e => {
                          const updated = [...adminCustomerCareNumbers];
                          updated[idx].whatsapp = e.target.value;
                          setAdminCustomerCareNumbers(updated);
                        }}
                        style={{ flex: '0 0 140px', minWidth: '100px' }}
                      />
                      <button
                        onClick={() => {
                          const updated = adminCustomerCareNumbers.filter((_, i) => i !== idx);
                          setAdminCustomerCareNumbers(updated);
                        }}
                        style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, flex: '0 0 auto' }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setAdminCustomerCareNumbers([...adminCustomerCareNumbers, { country: '', phone: '', whatsapp: '' }])}
                  className="btn btn-secondary w-full"
                  style={{ justifyContent: 'center', marginBottom: 12 }}
                >
                  + Add Contact
                </button>

                <button
                  onClick={handleSaveCustomerCareNumbers}
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center' }}
                >
                  💾 Save Contacts
                </button>

                <div style={{ marginTop: 16, padding: 12, background: 'var(--surface)', borderRadius: 8, borderLeft: '3px solid var(--teal)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--teal)', marginBottom: 8 }}>📞 Phone & 💬 WhatsApp Preview:</div>
                  {adminCustomerCareNumbers.length > 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.8 }}>
                      {adminCustomerCareNumbers.map((num, idx) => (
                        <div key={idx} style={{ marginBottom: 8 }}>
                          <strong>{num.country || '(country)'}</strong>
                          <div style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink3)' }}>
                            {num.phone && <div>📞 {num.phone}</div>}
                            {num.whatsapp && <div>💬 {num.whatsapp}</div>}
                            {!num.phone && !num.whatsapp && <div style={{ color: 'var(--ink4)' }}>No contacts set</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--ink4)' }}>No contacts added yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Language Modal */}
      <Modal
        open={showLangModal}
        onClose={() => { setShowLangModal(false); setEditingLang(null); setLangForm({name:'', code:''}); }}
        title={editingLang ? 'Edit Language' : 'Add New Language'}
        subtitle="Languages available for expert profiles"
      >
        <div className="form-group">
          <label className="form-label">Language Name</label>
          <input className="form-input" placeholder="e.g. Hindi" value={langForm.name} onChange={e => setLangForm(f => ({...f, name:e.target.value}))} />
        </div>
        <div className="form-group">
          <label className="form-label">Language Code</label>
          <input className="form-input" placeholder="e.g. hi" value={langForm.code} onChange={e => setLangForm(f => ({...f, code:e.target.value}))} style={{ fontFamily:'var(--font-mono)' }} />
        </div>
        <button onClick={handleSaveLang} className="btn btn-primary w-full" style={{ justifyContent:'center', marginTop:8 }}>
          {editingLang ? 'Update Language' : 'Add Language'}
        </button>
      </Modal>

      {/* Category Modal */}
      <Modal
        open={showCatModal}
        onClose={() => { setShowCatModal(false); setEditingCat(null); setCatModalForm({name:'', icon:''}); }}
        title={editingCat ? 'Edit Category' : 'Add New Category'}
        subtitle="Service categories for expert listings"
      >
        <div className="form-group">
          <label className="form-label">Category Name</label>
          <input className="form-input" placeholder="e.g. Financial Coaching" value={catModalForm.name} onChange={e => setCatModalForm(f => ({...f, name:e.target.value}))} />
        </div>
        <div className="form-group">
          <label className="form-label">Icon (emoji)</label>
          <input className="form-input" placeholder="💰" value={catModalForm.icon} onChange={e => setCatModalForm(f => ({...f, icon:e.target.value}))} style={{ fontSize:20 }} />
        </div>
        <button onClick={handleSaveCat} className="btn btn-primary w-full" style={{ justifyContent:'center', marginTop:8 }}>
          {editingCat ? 'Update Category' : 'Add Category'}
        </button>
      </Modal>

      {/* Subcategory Modal */}
      <Modal
        open={showSubModal}
        onClose={() => { setShowSubModal(false); setEditingSub(null); setSubForm({name:''}); }}
        title={editingSub ? 'Edit Subcategory' : 'Add Subcategory'}
        subtitle={catData.find(c => c.id === subModalCatId)?.name || ''}
      >
        <div className="form-group">
          <label className="form-label">Subcategory Name</label>
          <input className="form-input" placeholder="e.g. Tax Planning" value={subForm.name} onChange={e => setSubForm(f => ({...f, name:e.target.value}))} />
        </div>
        <button onClick={handleSaveSub} className="btn btn-primary w-full" style={{ justifyContent:'center', marginTop:8 }}>
          {editingSub ? 'Update Subcategory' : 'Add Subcategory'}
        </button>
      </Modal>

      {/* Tag Modal */}
      <Modal
        open={showTagModal}
        onClose={() => { setShowTagModal(false); setEditingTag(null); setTagForm({name:''}); }}
        title={editingTag ? 'Edit Tag' : 'Add New Tag'}
        subtitle="Tags help learners find the right expert"
      >
        <div className="form-group">
          <label className="form-label">Tag Name</label>
          <input className="form-input" placeholder="e.g. Beginner Friendly" value={tagForm.name} onChange={e => setTagForm({ name: e.target.value })} onKeyDown={e => e.key === 'Enter' && handleSaveTag()} />
        </div>
        <button onClick={handleSaveTag} className="btn btn-primary w-full" style={{ justifyContent:'center', marginTop:8 }}>
          {editingTag ? 'Update Tag' : 'Add Tag'}
        </button>
      </Modal>

      {/* User Details Modal */}
      <Modal 
        open={showUserDetails} 
        onClose={() => { setShowUserDetails(false); setShowPasswordChange(false); setNewPassword(''); }}
        title={`${selectedUser?.name} - Details`}
        subtitle={`Manage user account and settings`}
      >
        {selectedUser && (
          <div style={{ padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div>
                <h4 style={{ fontWeight: 600, marginBottom: 8, color: 'var(--ink)' }}>Basic Information</h4>
                <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 4 }}>
                  <strong>Name:</strong> {selectedUser.name}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 4 }}>
                  <strong>Email:</strong> {selectedUser.email}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 4 }}>
                  <strong>Mobile:</strong> {selectedUser.mobile}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 4 }}>
                  <strong>City:</strong> {selectedUser.city || 'Not specified'}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 4 }}>
                  <strong>Role:</strong> <span className={`badge ${selectedUser.role === 'expert' ? 'badge-teal' : 'badge-saffron'}`}>{selectedUser.role}</span>
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 4 }}>
                  <strong>Joined:</strong> {selectedUser.joined}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink3)' }}>
                  <strong>Status:</strong>{' '}
                  <span className="badge" style={{
                    background: selectedUser.status === 'active' ? '#dcfce7' : '#fee2e2',
                    color: selectedUser.status === 'active' ? '#15803d' : '#dc2626',
                  }}>{selectedUser.status}</span>
                </div>
              </div>

              <div>
                <h4 style={{ fontWeight: 600, marginBottom: 16, color: 'var(--ink)' }}>Account Actions</h4>

                {!showPasswordChange ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <button
                      onClick={() => { handleToggleStatus(selectedUser); }}
                      className="btn"
                      style={{
                        justifyContent: 'center',
                        background: selectedUser.status === 'active' ? '#FEF3C7' : '#DCFCE7',
                        color: selectedUser.status === 'active' ? '#B45309' : '#15803D',
                        border: '1px solid currentColor',
                      }}
                    >
                      {selectedUser.status === 'active' ? '⏸ Deactivate Account' : '▶ Activate Account'}
                    </button>
                    <button
                      onClick={() => setShowPasswordChange(true)}
                      className="btn btn-secondary"
                      style={{ justifyContent: 'center' }}
                    >
                      🔑 Change Password
                    </button>
                    <button
                      onClick={() => handleDeleteUser(selectedUser.id)}
                      className="btn btn-danger"
                      style={{ justifyContent: 'center' }}
                    >
                      🗑️ Delete User
                    </button>
                  </div>
                ) : (
                  <div>
                    <h5 style={{ fontWeight: 600, marginBottom: 12, color: 'var(--ink)' }}>Change Password</h5>
                    <div className="form-group" style={{ marginBottom: 16 }}>
                      <label className="form-label">New Password</label>
                      <input
                        className="form-input"
                        type="password"
                        placeholder="Enter new password (min 6 characters)"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button 
                        onClick={handleChangePassword}
                        className="btn btn-primary"
                        style={{ flex: 1, justifyContent: 'center' }}
                        disabled={!newPassword || newPassword.length < 6}
                      >
                        Update Password
                      </button>
                      <button 
                        onClick={() => { setShowPasswordChange(false); setNewPassword(''); }}
                        className="btn btn-secondary"
                        style={{ flex: 1, justifyContent: 'center' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
      </div>
    </div>
  );
}
